import { styled } from 'stitches.config'
import { Stack } from '../../components/Stack'
import { buttonReset } from 'components/Button/Button.styles'
import { BorderWave } from 'components/BorderWave'

export const OurStoryRoot = styled('div', {
  '& > section': {
    position: 'relative',
    paddingLeft: '$3',
    paddingRight: '$3',
    overflow: 'hidden',
    '@bp3': {
      paddingLeft: '15.9vw',
      paddingRight: '15.9vw',
    },
  },
  '& h2': {
    fontSize: '$h2',
    fontWeight: '$medium',
    position: 'relative',
    zIndex: '1',
    textWrap: 'balance',
    '@bp2': {
      fontSize: '$h1',
    },
  },
  '& h3': {
    fontFamily: '$primary',
    fontSize: '$h3',
    fontWeight: '$medium',
    color: '$primary-450',
    '@bp2': {
      fontSize: '$h3',
    },
  },
  '& p': {
    fontFamily: '$primary',
    fontSize: '$body',
    color: '$quaternary-200',
    fontWeight: '$regular',
    textAlign: 'center',
    '@bp2': {
      fontSize: '$bodyLarge',
      textAlign: 'left',
    },
  },
  '& p.caption': {
    fontSize: '$body',
  },
  '& b': {
    fontFamily: '$primary',
    fontSize: '$body',
    fontWeight: '$medium',
    color: '$primary-450',
    '@bp2': {
      fontSize: '$bodyLarge',
    },
  },
  '& .arrow': {
    cursor: 'pointer',
    border: 'none',
  },
  '& img': {
    borderRadius: '15px',
    '@bp2': {
      borderRadius: '20px',
    },
  },
})

export const PhotoCaption = styled('span', {
  fontSize: '$small',
  color: '$accent-white',
  textAlign: 'center',
  display: 'block',
  position: 'absolute',
  width: '100%',
  variants: {
    position: {
      top: {
        left: '50%',
        transform: 'translateX(-50%)',
        top: '.6rem',
      },
      bottom: {
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: '.6rem',
      },
      bottomLarge: {
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: '10%',
      },
      bottomLeft: {
        bottom: '.6rem',
        left: '.6rem',
        '@bp2': {
          bottom: '15%',
          left: '32%',
          transform: 'translateX(-32%)',
        },
        '@bp4': {
          bottom: '15%',
          left: '25%',
          transform: 'translateX(-25%)',
        },
      },
      bottomRight: {
        bottom: '2rem',
        right: '2rem',
        width: 'auto',
      },
      bottomTop: {
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: '.6rem',
      },
    },
    mobileOnly: {
      true: {
        display: 'block',
        '@bp2': {
          display: 'none',
        },
      },
    },
  },
})

export const WhiteBlobContainer = styled('div', {
  display: 'none',
  '@bp2': {
    position: 'absolute',
    zIndex: '0',
    display: 'block',
  },
  variants: {
    location: {
      1: {
        top: '10rem',
        right: '-20rem',
      },
      2: {
        bottom: '3rem',
        left: '-20rem',
      },
      3: {
        top: '-16rem',
        left: '-22rem',
      },
      4: {
        top: '13rem',
        right: '-14rem',
      },
      5: {
        top: '-35%',
        left: '-11rem',
      },
      6: {
        bottom: '-1%',
        right: '-8rem',
      },
      7: {
        left: '-20rem',
        bottom: '-17rem',
      },
      8: {
        right: '0',
        top: '2rem',
      },
    },
    rotation: {
      1: {
        transform: 'rotate(0deg)',
      },
      2: {
        transform: 'rotate(-30.4deg)',
      },
    },
    color: {
      secondary: {
        '& img': {
          fill: '$secondary-100 !important',
        },
      },
    },
  },
})

export const SelectorsContainer = styled(Stack, {
  justifyContent: 'center',
  display: 'block',
  textAlign: 'center',
  '@bp2': {
    display: 'none',
  },
  variants: {
    padding: {
      med: {
        padding: '$3 0',
      },
    },
  },
})

export const Selector = styled('button', {
  ...buttonReset,
  cursor: 'pointer',
  size: '$2',
  borderRadius: '$round',
  backgroundColor: '$tertiary-250 !important',
  variants: {
    active: {
      true: {
        backgroundColor: '$primary-450 !important',
      },
    },
  },
})

export const FeatureRow = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  gap: '$3',
  paddingTop: '$6',
  '@bp2': {
    justifyContent: 'flex-start',
    flexWrap: 'nowrap',
    gap: '$8',
  },
})

export const FeatureColumn = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  alignItems: 'center',
  width: '45%',
  gap: '$3',
  '@bp3': {
    width: '12%',
  },
})

export const IconContainer = styled('div', {
  position: 'relative',
  height: '60px',
  '& img': {
    borderRadius: '0px',
  },
  variants: {
    adjusted: {
      tiny: {
        paddingTop: '.1rem',
      },
      small: {
        paddingTop: '.3rem',
      },
    },
  },
})

export const Badge = styled('div', {
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: '5',
  top: '-4rem',
  '& img': {
    height: '7.4rem !important',
    width: '7.4rem !important',
  },
})

export const DesktopBorderWave = styled(BorderWave, {
  display: 'none',
  '@bp2': {
    display: 'block',
  },
  backgroundColor: '$secondary-100',
  zIndex: '0',
  variants: {
    backgroundColor: {
      transparent: {
        backgroundColor: '$transparent',
      },
    },
    custom: {
      true: {
        position: 'absolute',
        left: '0',
        top: '-2.2rem',
        width: '100%',
        height: '37px',
      },
    },
  },
})

export const MobileBorderWave = styled(BorderWave, {
  display: 'block',
  backgroundColor: '$secondary-100',
  zIndex: '0',
  '& svg': {
    transform: 'scaleX(-1)',
    transformBox: 'fill-box',
    transformOrigin: 'center',
    left: '-42%',
  },
  '@bp2': {
    display: 'none',
  },
  variants: {
    backgroundColor: {
      secondary150: {
        backgroundColor: '$secondary-150',
      },
    },
    marginBottomAdjustment: {
      true: {
        marginBottom: '-2px',
      },
    },
  },
})